import { Component, input, model } from '@angular/core';

@Component({
  selector: 'app-date-time-input',
  template: `
    <div class="relative">
      <input
        data-testid="date-time-input"
        #picker
        [id]="id()"
        [type]="type()"
        [name]="name()"
        [value]="value()"
        (input)="onInputChange($event)"
        (blur)="validateDateTime()"
        (keydown.enter)="$event.preventDefault()"
        class="input w-full rounded-lg px-3 placeholder-gray-300 text-base"
        [class.input-error]="errorMessage()"
        (click)="picker.showPicker()"
        [required]="isRequired()"
        [min]="getMinValue()"
        [max]="getMaxValue()" />

      @if (errorMessage()) {
        <div
          class="absolute top-full left-0 text-error text-sm mt-1 z-1 bg-white">
          {{ errorMessage() }}
        </div>
      }
    </div>
  `,
})
export class DateTimeInput {
  type = input.required<string>();
  name = input.required<string>();
  isRequired = input<boolean>(false);
  id = input<string>('date-time-input');

  // Two-way binding with model()
  value = model<string>('');

  errorMessage = model<string>('');

  getMinValue(): string | null {
    const now = new Date();
    if (this.type() === 'datetime-local') {
      return now.toISOString().slice(0, 16);
    }
    return now.toISOString().slice(0, 10); // No min for normal date
  }

  getMaxValue(): string | null {
    if (this.type() === 'datetime-local') {
      const max = new Date();
      max.setHours(max.getHours() + 72, 0, 0, 0);
      return max.toISOString().slice(0, 16);
    }
    return null; // No max for normal date
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const newValue = target.value;

    console.log('Input changed:', newValue);
    this.clearError();
    this.value.set(newValue);
  }

  validateDateTime(): void {
    const currentValue = this.value();

    if (!currentValue) {
      if (this.type() === 'datetime-local') {
        this.setErrorIfRequired('Please select a date and time');
      } else {
        this.setErrorIfRequired('Please select a date');
      }
      return;
    }

    const selectedDate = new Date(currentValue);
    const now = new Date();
    const minValue = this.getMinValue();
    const maxValue = this.getMaxValue();

    if (this.type() === 'datetime-local') {
      const minDate = minValue ? new Date(minValue) : null;
      const maxDate = maxValue ? new Date(maxValue) : null;

      if (minDate && selectedDate < minDate) {
        this.errorMessage.set(
          'Please select a date and time within the next 72 hours'
        );
        return;
      }
      if (maxDate && selectedDate > maxDate) {
        this.errorMessage.set(
          'Please select a date and time within the next 72 hours'
        );
        return;
      }
    } else if (this.isDateInPast(selectedDate, now)) {
      this.errorMessage.set('Please select a future date');
      return;
    }

    this.clearError();
  }

  // Method to reset the value
  reset(): void {
    this.value.set('');
    this.clearError();
  }

  private isDateInPast(selectedDate: Date, now: Date): boolean {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDay = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    );
    return selectedDay < today;
  }

  private setErrorIfRequired(message: string): void {
    if (this.isRequired()) {
      this.errorMessage.set(message);
    }
  }

  private clearError(): void {
    if (this.errorMessage()) {
      this.errorMessage.set('');
    }
  }
}
